import React, { useMemo } from "react";
import { FaLinkedin } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const getTeamMemberImage = (name, teamImages) => {
  const lowerName = name.toLowerCase();

  if (lowerName.includes("ilija") || lowerName.includes("илия")) {
    return teamImages.ilija;
  }

  if (lowerName.includes("ilko") || lowerName.includes("илко")) {
    return teamImages.ilko;
  }

  if (lowerName.includes("evgeni") || lowerName.includes("евгени")) {
    return teamImages.evgeni;
  }

  return teamImages.ilija;
};

const TeamMember = React.memo(({ member, subtitle, teamImages }) => (
  <div className="bg-gray-800 rounded-2xl p-4 md:p-5 lg:p-6 w-full max-w-[280px] flex flex-col justify-between flex-1 min-h-[250px] mx-auto md:mx-0">
    <div className="flex flex-col-reverse sm:flex-row justify-between items-center sm:items-start gap-4 mb-4">
      <div className="text-center sm:text-left">
        <p className="text-transparent bg-clip-text bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450] mb-1 text-sm md:text-base">
          {subtitle}
        </p>
        <h3 className="text-lg md:text-xl font-medium mb-1">{member.name}</h3>
        <p className="text-gray-400 text-sm">{member.position}</p>
      </div>
      <img
        src={getTeamMemberImage(member.name, teamImages)}
        alt={member.name}
        className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-full"
        loading="lazy"
        decoding="async"
      />
    </div>
    <div className="flex justify-center sm:justify-start gap-2">
      <a
        href={member.linkedIn}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${member.name}'s LinkedIn profile`}
        className="p-2 text-[#0A66C2] hover:scale-105 transition-transform focus:ring-2 focus:ring-blue-400 focus:outline-none rounded-md"
      >
        <FaLinkedin size={20} />
      </a>
    </div>
  </div>
));

TeamMember.displayName = "TeamMember";

TeamMember.propTypes = {
  member: PropTypes.shape({
    name: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    linkedIn: PropTypes.string.isRequired,
  }).isRequired,
  subtitle: PropTypes.string.isRequired,
  teamImages: PropTypes.shape({
    ilija: PropTypes.string.isRequired,
    ilko: PropTypes.string.isRequired,
    evgeni: PropTypes.string.isRequired,
  }).isRequired,
};

function Team({ teamImages }) {
  const { t } = useTranslation();
  const teamData = useMemo(
    () => t("components.aboutUs.team", { returnObjects: true }),
    [t]
  );
  const { title, subtitle, members } = teamData;

  return (
    <div
      className="mx-auto w-full px-4 py-8 md:py-12 lg:py-16 bg-black text-white overflow-hidden"
      id="safePad"
    >
      <h2
        className="text-2xl lg:text-3xl text-center font-medium mb-6 md:mb-10 lg:mb-12"
        dangerouslySetInnerHTML={{ __html: title }}
      />

      <div className="flex flex-col md:flex-row md:justify-center gap-4 lg:gap-6 items-center md:items-stretch">
        {members.map((member, index) => (
          <TeamMember
            key={member.name || index}
            member={member}
            subtitle={subtitle}
            teamImages={teamImages}
          />
        ))}
      </div>
    </div>
  );
}

Team.propTypes = {
  teamImages: PropTypes.shape({
    ilija: PropTypes.string.isRequired,
    ilko: PropTypes.string.isRequired,
    evgeni: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(Team);
